/**
 * YouTube Analytics Dashboard page.
 * Shows channel overview, daily views trend and recent videos list.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import PreviewBanner from "@/components/preview-banner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { ArrowLeft, ExternalLink, Eye, ThumbsUp, MessageCircle, Clock, Users } from "lucide-react";

const RANGE_OPTIONS: Array<{ labelKey: string; days: number }> = [
  { labelKey: "youtubeAnalytics.range.7d", days: 7 },
  { labelKey: "youtubeAnalytics.range.28d", days: 28 },
  { labelKey: "youtubeAnalytics.range.90d", days: 90 },
];

interface ChannelStats {
  channelId: string;
  title: string;
  thumbnailUrl: string | null;
  subscriberCount: number;
  viewCount: number;
  videoCount: number;
}

interface ChannelReportRow {
  views: number;
  estimatedMinutesWatched: number;
  averageViewDuration: number;
  subscribersGained: number;
  subscribersLost: number;
  likes: number;
  comments: number;
  shares: number;
}

interface DailyPoint {
  day: string;
  views: number;
  estimatedMinutesWatched: number;
  subscribersGained: number;
  subscribersLost: number;
}

interface AnalyticsResponse {
  channel: ChannelStats | null;
  report: { startDate: string; endDate: string; totals: ChannelReportRow };
  daily: { startDate: string; endDate: string; points: DailyPoint[] };
  preview?: { enabled: boolean; reason: string; source: string } | null;
}

interface RecentVideo {
  videoId: string;
  title: string;
  publishedAt: string;
  thumbnailUrl: string | null;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  durationSeconds: number;
  isShort: boolean;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("pl-PL").format(n);
}

function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return "0:00";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const padded = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${h}:${padded(m)}:${padded(s)}`;
  return `${m}:${padded(s)}`;
}

function formatMinutesAsHours(minutes: number, t: (k: string) => string): string {
  if (minutes <= 0) return `0 ${t("youtubeAnalytics.unit.min")}`;
  const hours = minutes / 60;
  if (hours < 1) return `${formatNumber(minutes)} ${t("youtubeAnalytics.unit.min")}`;
  return `${hours.toFixed(1)} ${t("youtubeAnalytics.unit.h")}`;
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function StatCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            {icon}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-muted-foreground">{label}</div>
            <div className="text-2xl font-bold truncate">{value}</div>
            {hint ? <div className="text-xs text-muted-foreground mt-1">{hint}</div> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Skeleton siatki 4 kafelków statystyk — odwzorowuje StatCard (ikona + label + wartość). */
function StatGridSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
              <div className="min-w-0 flex-1">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-7 w-20" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Skeleton karty wykresu dziennych wyświetleń (nagłówek + obszar h-64). */
function ChartCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-64 w-full" />
      </CardContent>
    </Card>
  );
}

/** Skeleton listy ostatnich filmów — miniatura + dwie linie tekstu na wiersz. */
function VideoListSkeleton() {
  return (
    <ul className="divide-y">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="py-3 flex items-center gap-3">
          <Skeleton className="w-24 h-14 rounded shrink-0" />
          <div className="flex-1 min-w-0">
            <Skeleton className="h-4 w-2/3 mb-2" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function YoutubeAnalyticsPage() {
  const { t } = useTranslation();
  const [rangeDays, setRangeDays] = useState<number>(28);
  const startDate = useMemo(() => daysAgoIso(rangeDays), [rangeDays]);
  const forcePreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("preview") === "1";
  }, []);

  const statusQuery = useQuery<{ connected: boolean; channelTitle?: string | null }>({
    queryKey: ["/api/integrations/youtube/status"],
    queryFn: () => apiRequest("GET", "/api/integrations/youtube/status"),
  });

  const isConnected = statusQuery.data?.connected === true;

  const analyticsQuery = useQuery<AnalyticsResponse>({
    queryKey: ["/api/integrations/youtube/analytics", startDate, forcePreview],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/integrations/youtube/analytics?startDate=${startDate}${forcePreview ? "&preview=1" : ""}`
      ),
    enabled: !statusQuery.isLoading,
  });

  const videosQuery = useQuery<{
    videos: RecentVideo[];
    preview?: { enabled: boolean; reason: string; source: string } | null;
  }>({
    queryKey: ["/api/integrations/youtube/videos", forcePreview],
    queryFn: () =>
      apiRequest(
        "GET",
        `/api/integrations/youtube/videos?limit=12${forcePreview ? "&preview=1" : ""}`
      ),
    enabled: !statusQuery.isLoading,
  });

  if (statusQuery.isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link href="/integracje" className="p-2 rounded-md hover:bg-accent">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">YouTube Analytics</h1>
              <Skeleton className="h-4 w-40 mt-1" />
            </div>
          </div>
          <div className="flex gap-2">
            {RANGE_OPTIONS.map((opt) => (
              <Skeleton key={opt.days} className="h-8 w-16 rounded-md" />
            ))}
          </div>
        </div>
        <StatGridSkeleton />
        <ChartCardSkeleton />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("youtubeAnalytics.recentVideos")}</CardTitle>
          </CardHeader>
          <CardContent>
            <VideoListSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  const channel = analyticsQuery.data?.channel ?? null;
  const totals = analyticsQuery.data?.report.totals;
  const daily = analyticsQuery.data?.daily.points ?? [];
  const netSubs = totals ? totals.subscribersGained - totals.subscribersLost : 0;
  const previewEnabled = analyticsQuery.data?.preview?.enabled === true;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link href="/integracje" className="p-2 rounded-md hover:bg-accent">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">YouTube Analytics</h1>
            {channel ? (
              <div className="text-sm text-muted-foreground">{channel.title}</div>
            ) : isConnected ? (
              <div className="text-sm text-muted-foreground">
                {statusQuery.data?.channelTitle || t("youtubeAnalytics.connectedChannel")}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex gap-2">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              onClick={() => setRangeDays(opt.days)}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                rangeDays === opt.days
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background hover:bg-accent border-border"
              }`}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {previewEnabled && <PreviewBanner description={t("youtubeAnalytics.previewDescription")} />}

      {analyticsQuery.isLoading ? (
        <>
          <StatGridSkeleton />
          <ChartCardSkeleton />
        </>
      ) : analyticsQuery.isError ? (
        !isConnected ? (
          <Card>
            <CardContent className="p-8 text-center space-y-4">
              <h2 className="text-xl font-semibold">{t("youtubeAnalytics.notConnected.title")}</h2>
              <p className="text-muted-foreground">
                {t("youtubeAnalytics.notConnected.description")}
              </p>
              <Link
                href="/integracje"
                className="inline-flex items-center gap-2 text-primary underline"
              >
                {t("youtubeAnalytics.notConnected.cta")} <ExternalLink className="w-4 h-4" />
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-6 text-center text-sm text-destructive">
              {t("youtubeAnalytics.fetchError")} <code>yt-analytics.readonly</code>
              {t("youtubeAnalytics.fetchErrorSuffix")}
            </CardContent>
          </Card>
        )
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Users className="w-5 h-5" />}
              label={t("youtubeAnalytics.stat.subscribers")}
              value={channel ? formatNumber(channel.subscriberCount) : "—"}
              hint={
                totals
                  ? `${netSubs >= 0 ? "+" : ""}${formatNumber(netSubs)} ${t("youtubeAnalytics.stat.inPeriod")}`
                  : undefined
              }
            />
            <StatCard
              icon={<Eye className="w-5 h-5" />}
              label={t("youtubeAnalytics.stat.views")}
              value={totals ? formatNumber(totals.views) : "—"}
              hint={t("youtubeAnalytics.stat.inSelectedPeriod")}
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              label={t("youtubeAnalytics.stat.watchTime")}
              value={totals ? formatMinutesAsHours(totals.estimatedMinutesWatched, t) : "—"}
              hint={
                totals
                  ? `${t("youtubeAnalytics.stat.avg")} ${formatDuration(totals.averageViewDuration)} / ${t("youtubeAnalytics.stat.viewShort")}`
                  : undefined
              }
            />
            <StatCard
              icon={<ThumbsUp className="w-5 h-5" />}
              label={t("youtubeAnalytics.stat.likes")}
              value={totals ? formatNumber(totals.likes) : "—"}
              hint={
                totals
                  ? `${formatNumber(totals.comments)} ${t("youtubeAnalytics.stat.comments")}`
                  : undefined
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("youtubeAnalytics.dailyViews")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={daily} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="views"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("youtubeAnalytics.recentVideos")}</CardTitle>
        </CardHeader>
        <CardContent>
          {videosQuery.isLoading ? (
            <VideoListSkeleton />
          ) : videosQuery.data && videosQuery.data.videos.length > 0 ? (
            <ul className="divide-y">
              {videosQuery.data.videos.map((video) => (
                <li key={video.videoId} className="py-3 flex items-center gap-3">
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt=""
                      className="w-24 h-14 object-cover rounded"
                    />
                  ) : (
                    <div className="w-24 h-14 bg-muted rounded" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://www.youtube.com/watch?v=${video.videoId}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium truncate hover:underline"
                      >
                        {video.title}
                      </a>
                      {video.isShort ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/10 text-red-500">
                          SHORT
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {formatNumber(video.viewCount)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        {formatNumber(video.likeCount)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {formatNumber(video.commentCount)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(video.durationSeconds)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center">
              {t("youtubeAnalytics.noVideos")}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
